import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from "@angular/core";
import { FRONTSIDEBAR_SELECTOR } from "../../../crossconcern/utilities/properties/selector.property";
import { BaseComponent } from "../../base/base.component";
import { SidebarItemModel } from "../../../crossconcern/models/sidebarItem.model";
import { AppComponent } from "../../app.component";
import { Router } from "@angular/router";
import { Location } from "@angular/common";
import { AlertUtils } from "../../../crossconcern/utilities/utils/alert.utils";
import { NURSERIES_PATH, SPHERE_PATH } from "../../../crossconcern/utilities/properties/path.property";
import { ProductModel } from "../../../datastore/entities/product.model";
import { PhotosphereManager } from "../../../business/managers/photosphere.manager";
import { NurseryModel } from "../../../datastore/entities/nursery.model";
import { logData } from "../../../crossconcern/helpers/generic/generic.helper";
import {
    FRONTOFFICE_LOGIN_ROUTE,
    MAP_ROUTE,
    NURSERIES_ROUTE
} from "../../../crossconcern/utilities/properties/routename.property";
import { UserManager } from "../../../business/managers/user.manager";
import {
    LANG_CODE_EN, LANG_CODE_IT,
    VIRTUAL_TOUR_CONTACT_EMAIL,
    VIRTUAL_TOUR_FAQ_URL,
    SEARCH_DEBOUNCE_TIME,
    SEARCH_LETTER_TRESHOLD, LANG_CODE_FR
} from "../../../crossconcern/utilities/properties/base.property";
import { TranslateService } from "@ngx-translate/core";
import { switchMap, distinctUntilChanged, debounceTime } from "rxjs/operators";
import { Subject } from "rxjs";

@Component({
    selector: FRONTSIDEBAR_SELECTOR,
    templateUrl: "./frontsidebar.component.html"
})
export class FrontsidebarComponent extends BaseComponent implements OnInit, OnDestroy {
    @Output()
    public onItemClick: EventEmitter<SidebarItemModel> = new EventEmitter();

    @Output() public onSidebarLoaded: EventEmitter<string> = new EventEmitter();
    @Output() public onSidebarToggle: EventEmitter<string> = new EventEmitter();

    @Input() public nurseries: NurseryModel[] = [];
    @Input() public isInsideNursery: boolean;
    @Input() public isInsidePhotosphere: boolean;
    @Input() public isBack: boolean;

    public searchSubject = new Subject<string>();
    public searchText: string;
    public lastText: string;

    public email: string;
    public faqUrl: string;

    public products: ProductModel[];

    public placeholder = "SEARCHBAR_COMPONENT_BUTTON_PLACEHOLDER";
    public emptySearchLabel: string;

    constructor(protected appComponent: AppComponent,
        protected router: Router,
        protected location: Location,
        protected userManager: UserManager,
        protected photosphereManager: PhotosphereManager,
        protected translate: TranslateService,
        protected alertUtils: AlertUtils) {
        super(appComponent, router, alertUtils, location);
    }

    public ngOnInit(): void {
        this.emptySearchLabel = this.isInsideNursery ? "PRODUCTS_INFO_TEXT" : "NURSERIES_INFO_TEXT";
        this.email = VIRTUAL_TOUR_CONTACT_EMAIL;
        const language = this.photosphereManager.getCurrentLanguage();
        this.faqUrl = VIRTUAL_TOUR_FAQ_URL.replace("/it/", "/" + language + "/");
        this.setSearchSubject();
        this.onSidebarLoaded.emit();
    }

    public ngOnDestroy(): void {
        if(this.searchSubject != null && !this.searchSubject.closed) {
            this.searchSubject.unsubscribe();
        }
    }

    public onSearch() {
        let search = "";
        if (this.isInsideNursery) {
            const searchTerms = this.searchText.split(" ");
            searchTerms.forEach((term) => {
                search += "var_dex:" + term + ",";
            });
        } else {
            search = "productOrNurseryName:" + this.searchText.replace(/\s/g, "---");
        }
        this.searchSubject.next(search);
    }

    public textChange() {
        if(this.searchText === "" && this.lastText !== "") {
            this.focusOut();
        }
        this.lastText = this.searchText;

        if (this.searchText !== undefined && this.searchText.length > SEARCH_LETTER_TRESHOLD) {
            this.onSearch();
        } else {
            this.products = null;
        }
    }

    public setSearchSubject() {
        this.searchSubject.pipe(
            debounceTime(SEARCH_DEBOUNCE_TIME),
            distinctUntilChanged(),
            switchMap((search) => {
                return this.photosphereManager.getProducts(search);
            })
        ).subscribe((prods) => {
            this.products = [];
            this.products = prods.filter((item) => {
                if (this.isInsideNursery) {
                    if (item.photospheres.length > 0) {
                        for (const photosphere of item.photospheres) {
                            if (photosphere.nurseryUuid === this.nurseries[0].uuid)
                                return true;
                        }
                        return false;
                    }
                    return false;
                }
                return item.photospheres.length > 0;
            });
            for(const product of this.products) {
                product.photospheres.sort(function(x, y) {
                    if (x.uuid > y.uuid) return -1;
                    if (x.uuid < y.uuid) return 1;
                    return 0;
                });
                product.nurseries = [];
                for(const photosphere of product.photospheres) {
                    const nursery = this.nurseries.find(function(item) {
                        return item.uuid === photosphere.nurseryUuid;
                    });
                    product.nurseries.push(nursery);
                }
            }
            logData(this.nurseries);
        });
    }

    public focusOut() {
        this.products = null;
        this.searchText = "";
    }

    public itemClicked(item, nursery, nurseryIndex) {
        this.appComponent.startLoading();
        window.location.href = NURSERIES_PATH + "/" + SPHERE_PATH + "/" +
            nursery.uuid + "?start=" + item.photospheres[nurseryIndex].uuid;
    }

    public onBackClick() {
        if (this.isInsidePhotosphere) {
            this.goToRouteByName(MAP_ROUTE, { id: this.nurseries[0].uuid });
        } else {
            this.goToRouteByName(NURSERIES_ROUTE);
        }
    }

    public onLogoutClick() {
        this.userManager.logout();
        this.goToRouteByName(FRONTOFFICE_LOGIN_ROUTE);
    }

    public setLanguage(language: string) {
        this.translate.use(language);
        this.userManager.setCurrentLanguage(language);
        this.faqUrl = VIRTUAL_TOUR_FAQ_URL.replace("/it/", "/" + language + "/");
    }

    public setLanguageEnglish() {
        this.setLanguage(LANG_CODE_EN);
    }

    public setLanguageItalian() {
        this.setLanguage(LANG_CODE_IT);
    }

    public setLanguageFrench() {
        this.setLanguage(LANG_CODE_FR);
    }

    public currentLangItalian(): boolean {
        const currentLang = this.translate.currentLang;
        if (currentLang === LANG_CODE_IT) {
            return true;
        }
        return false;
    }

    public currentLangEnglish(): boolean {
        const currentLang = this.translate.currentLang;
        if (currentLang === LANG_CODE_EN) {
            return true;
        }
        return false;
    }

    public currentLangFrench(): boolean {
        const currentLang = this.translate.currentLang;
        if (currentLang === LANG_CODE_FR) {
            return true;
        }
        return false;
    }

    public getProdName(productModel: ProductModel) {
        const language = this.photosphereManager.getCurrentLanguage();
        return this.getProductName(productModel, language);
    }
}
